import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, X, Loader2, ImagePlus, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { authHelper } from '../utils/authHelper';

export function AiImportModal({ onClose, onParsed }) {
  const { language } = useLanguage();
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadFile = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => setImageData(reader.result);
    reader.readAsDataURL(file);
    setError('');
  }, []);

  useEffect(() => {
    const handlePaste = (e) => {
      const item = Array.from(e.clipboardData?.items || []).find(i => i.type.startsWith('image/'));
      if (item) loadFile(item.getAsFile());
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [loadFile]);

  const analyze = async () => {
    if (!imageData) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/parse-bitacora-screenshot`, {
        method: 'POST',
        headers: authHelper.getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ image_base64: imageData })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Error');
      onParsed(json.data);
    } catch (e) {
      setError(e.message || (language === 'es' ? 'No se pudo analizar la imagen' : 'Could not analyze the image'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ai-import-modal">
      <div className="bg-white rounded-2xl shadow-2xl border border-violet-200 max-w-lg w-full p-6 space-y-4 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" data-testid="ai-import-close">
          <X size={20} />
        </button>
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Sparkles className="text-violet-600" size={20} />
          {language === 'es' ? 'Leer captura con IA' : 'Read screenshot with AI'}
        </h3>
        <p className="text-sm text-gray-500">
          {language === 'es'
            ? 'Pega (Ctrl+V) o sube la captura de tu plataforma de trading. La IA leerá las operaciones y rellenará el formulario para que lo revises antes de guardar.'
            : 'Paste (Ctrl+V) or upload your trading platform screenshot. The AI will read the trades and pre-fill the form for you to review before saving.'}
        </p>

        {imageData ? (
          <div className="relative border border-violet-200 rounded-xl overflow-hidden bg-gray-50">
            <img src={imageData} alt="screenshot" className="w-full max-h-56 object-contain" data-testid="ai-import-preview" />
            <button
              onClick={() => setImageData(null)}
              className="absolute top-2 right-2 bg-white/90 p-1 rounded-full text-gray-500 hover:text-red-600 shadow"
              data-testid="ai-import-remove-image"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <label
            className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-violet-300 rounded-xl p-8 cursor-pointer hover:bg-violet-50 transition-colors text-violet-500"
            data-testid="ai-import-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); loadFile(e.dataTransfer.files?.[0]); }}
          >
            <ImagePlus size={32} />
            <span className="text-sm font-semibold text-center">
              {language === 'es' ? 'Pega la imagen aquí (Ctrl+V), arrástrala o haz clic para subirla' : 'Paste the image here (Ctrl+V), drag it or click to upload'}
            </span>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => loadFile(e.target.files?.[0])} data-testid="ai-import-file-input" />
          </label>
        )}

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="ai-import-error">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        <button
          onClick={analyze}
          disabled={!imageData || loading}
          className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all ${
            !imageData || loading
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-br from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700 text-white shadow-md hover:shadow-lg active:scale-95'
          }`}
          data-testid="ai-import-analyze-btn"
        >
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              {language === 'es' ? 'Leyendo la captura...' : 'Reading the screenshot...'}
            </>
          ) : (
            <>
              <Sparkles size={18} />
              {language === 'es' ? 'Analizar con IA' : 'Analyze with AI'}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
